import { useOpenProjectSetup } from "commands/definitions/devtools/useOpenProjectSetup";
import { useOpenVMSpecs } from "commands/definitions/devtools/useOpenVMSpecs";
import { useExplorerDownloadCommand } from "commands/definitions/explorer/useExplorerDownloadCommand";
import { useColorSchemaCommand } from "commands/definitions/settings/useColorSchemaCommand";
import { useRestartCommand } from "commands/definitions/useRestartCommand";
import { useToggleCommandPaletteCommand } from "commands/definitions/useToggleCommandPaletteCommand";
import { useOpenEditorSettingsCommand } from "commands/definitions/vscode/useOpenEditorSettingsCommand";
import { useOpenKeyboardShortcutsCommand } from "commands/definitions/vscode/useOpenKeyboardShortcutsCommand";
import { useFeedbackGlobalModal } from "components/FeedbackForm/FeedbackFormDialog";
import { useProjectSettingsContext } from "components/Settings/SettingsProvider";
import { env } from "environment-interface/env";
import { useContainer } from "features/container/Feature";
import { useEditor } from "features/editor/Feature";
import { useAddStatusNotification } from "features/notifications/hooks";
import { usePitcher } from "features/utils/usePitcher";
import { useSession } from "hooks/api/useSession";
import { usePermissions } from "hooks/pitcher/usePermissions";
import {
  hasBranchWriteAccess,
  hasSandboxPermission,
} from "isomorphic/permission";
import {
  Box,
  Button,
  Dialog,
  Dropdown,
  Icon,
  Stack,
  styled,
} from "prism-react";
import * as React from "react";

import { getShortcut } from "commands/utils";
import { useChangelogAvailable } from "hooks/useChangelogAvailable";
import { paths } from "utils/router";
import { executeVSCodeCommand } from "utils/vscode";

import { ContainerVersionDetails } from "../VersionDetails/ContainerVersionDetails";

import { MenuItem } from "./MenuItem";

export const StyledShortcut = styled("div", {
  marginLeft: "auto",
  paddingLeft: "$2",
  fontSize: "$caption2",
});

export const MainMenu: React.FC = () => {
  const [session, sessionApi] = useSession();
  const [container] = useContainer();
  const project = useEditor();
  const pitcherPermissions = usePermissions();
  const pitcher = usePitcher();
  const addStatusNotification = useAddStatusNotification();

  const openEditorSettings = useOpenEditorSettingsCommand("main_menu");
  const openKeyboardShorcuts = useOpenKeyboardShortcutsCommand("main_menu");
  const openColorThemeSelector = useColorSchemaCommand();
  const openCommandPalette = useToggleCommandPaletteCommand("main_menu");
  const projectSetupCommand = useOpenProjectSetup();
  const openVMSpecsCommand = useOpenVMSpecs();
  const changelogStatus = useChangelogAvailable();

  const downloadAction = useExplorerDownloadCommand({
    type: project.type === "sandbox" ? "Sandbox" : "Branch",
  });
  const restartCommand = useRestartCommand();

  // Dialogs
  const feedbackModal = useFeedbackGlobalModal();
  const [showVersion, setShowVersion] = React.useState(false);

  const { setVisibility } = useProjectSettingsContext();

  const [restartEnabled, setRestartEnabled] = React.useState(false);

  React.useEffect(() => {
    setRestartEnabled(
      container.state !== "RESTARTING" &&
        project.allowedCapabilities.restartContainer,
    );
  }, [container]);

  const envVarTooltip = !(project.type === "branch"
    ? hasBranchWriteAccess(project.authorization)
    : hasSandboxPermission(project.authorization, "write_project"))
    ? "Environment variables are only available to users with write permissions."
    : undefined;

  const userIsSignedIn = session.state === "AUTHENTICATED";

  let projectName;
  if (project.type === "sandbox") {
    if (project.isCloud) {
      projectName = "Devbox";
    } else {
      projectName = "Sandbox";
    }
  } else {
    projectName = "branch";
  }

  return (
    <Box>
      <Dropdown
        maxHeight={false}
        trigger={
          <Button
            aria-label="CodeSandbox"
            css={{
              padding: "$1",
              border: 0,
              "&:hover": {
                color: "$neutral-fg-high",
                "& .chevron": {
                  translate: "0 2px",
                },
              },
            }}
            gap={0}
            icon="codesandbox"
            iconSize={6}
            role="button"
            tabIndex={0}
          >
            <Stack>
              <Icon
                className="chevron"
                css={{
                  transition: "translate $fast",
                }}
                size={4}
                type="chevronSmallDown"
                aria-hidden
              />
            </Stack>
          </Button>
        }
      >
        {userIsSignedIn && (
          <MenuItem
            as="a"
            href={paths.dashboard.recent()}
            icon="home"
            title="Dashboard"
          />
        )}

        <MenuItem
          icon="pages"
          onClick={(event) => {
            if (event.metaKey) {
              window.open(
                `https://codesandbox.io/docs/learn/${
                  project.type === "branch" ? "repositories" : "sandboxes"
                }/overview`,
                "_blank",
              );
            } else {
              executeVSCodeCommand("codesandbox.openCsbTab", {
                type: "DOCS",
              });
            }
          }}
          title="Documentation"
        />

        {userIsSignedIn && (
          <>
            <MenuItem
              icon="chat"
              onClick={feedbackModal.open}
              title="Feedback"
            />

            <Dropdown.Separator />
          </>
        )}

        <Dropdown.SubMenu
          trigger={
            <Stack css={{ width: "100%" }} horizontal>
              <Icon size={4} type="layoutDefault" />
              <span>Editor</span>
            </Stack>
          }
        >
          <MenuItem
            icon="user"
            meta={
              openEditorSettings.binding ? (
                <StyledShortcut>
                  {getShortcut(openEditorSettings.binding).join(" ")}
                </StyledShortcut>
              ) : undefined
            }
            onClick={openEditorSettings.perform}
            title={openEditorSettings.name}
          />
          <MenuItem
            icon="palette"
            onClick={() => {
              // Run it a tick later to ensure that the editor doesn't steal focus when opening menu
              setTimeout(() => {
                openColorThemeSelector.perform();
              }, 16);
            }}
            title="Color Theme"
          />
          <MenuItem
            icon="terminal"
            meta={
              <StyledShortcut>{getShortcut("$mod+K").join(" ")}</StyledShortcut>
            }
            onClick={() => {
              // Run it a tick later to ensure that the editor doesn't steal focus when opening menu
              setTimeout(() => {
                openCommandPalette.perform();
              }, 16);
            }}
            title={openCommandPalette.name}
          />
          <MenuItem
            icon="help"
            meta={
              openKeyboardShorcuts.binding ? (
                <StyledShortcut>
                  {getShortcut(openKeyboardShorcuts.binding).join(" ")}
                </StyledShortcut>
              ) : undefined
            }
            onClick={openKeyboardShorcuts.perform}
            title={openKeyboardShorcuts.name}
          />

          <Dropdown.Separator />

          <MenuItem
            icon="layoutSidebar"
            onClick={() => {
              executeVSCodeCommand("workbench.action.toggleSidebarVisibility");
            }}
            title="Toggle Sidebar"
          />
          <MenuItem
            icon="layoutShells"
            onClick={() => {
              executeVSCodeCommand("workbench.action.terminal.toggleTerminal");
            }}
            title="Toggle Tasks and Terminals"
          />
          <MenuItem
            icon="layoutDevtools"
            onClick={() => {
              executeVSCodeCommand("codesandbox.layout.toggleDevtools");
            }}
            title="Toggle Devtools"
          />
        </Dropdown.SubMenu>

        <Dropdown.Separator />

        {userIsSignedIn && (
          <>
            <MenuItem
              icon="settings"
              onClick={() => setVisibility(true)}
              title="Settings"
              tooltip={envVarTooltip}
            />

            {project.type === "sandbox" && !project.isCloud ? null : (
              <>
                <MenuItem
                  icon="machine"
                  onClick={openVMSpecsCommand.perform}
                  title={openVMSpecsCommand.name}
                />

                {project.type === "branch" && (
                  <MenuItem
                    icon="settings"
                    onClick={projectSetupCommand.perform}
                    title={projectSetupCommand.name}
                  />
                )}

                <MenuItem
                  disabled={!restartEnabled}
                  icon="clock"
                  onClick={() => {
                    addStatusNotification({
                      message:
                        "Starting hibernation, navigating to Dashboard shortly",
                      status: "warning",
                    });

                    pitcher.clients.system.hibernate();

                    setTimeout(() => {
                      window.location.href = paths.dashboard.recent();
                    }, 2000);
                  }}
                  title="Hibernate VM"
                />

                <MenuItem
                  disabled={!restartEnabled}
                  icon="refresh"
                  onClick={restartCommand.perform}
                  title={`Restart ${projectName}`}
                />
              </>
            )}
          </>
        )}

        <MenuItem
          disabled={!project.canDownload || !pitcherPermissions.fs?.download}
          icon="download"
          onClick={downloadAction.perform}
          title={`Download ${projectName}`}
          tooltip={
            !project.canDownload
              ? "The workspace has disabled exporting of files"
              : !pitcherPermissions.fs?.download
                ? "Restart the project to enable downloading"
                : undefined
          }
        />

        <Dropdown.Separator />

        <MenuItem
          icon="machine"
          onClick={() => setShowVersion(true)}
          title={`Version: ${env.PUBLIC_VERSION}`}
        />

        <MenuItem
          icon="community"
          meta={
            changelogStatus.status === "available" ? (
              <Icon
                css={{
                  color: "$accent-primary-base",
                }}
                size={3}
                type="dot"
              />
            ) : null
          }
          onClick={changelogStatus.open}
          title="Changelog"
        />

        <Dropdown.Separator />

        {userIsSignedIn ? (
          <MenuItem
            icon="signOut"
            onClick={() => sessionApi.signOut()}
            title="Sign out"
          />
        ) : (
          <MenuItem
            icon="signIn"
            onClick={() => sessionApi.signIn()}
            title={`Sign in`}
          />
        )}
      </Dropdown>

      {/* Version Modal */}
      <Dialog onOpenChange={setShowVersion} open={showVersion}>
        <Stack gap={5}>
          <Stack css={{ justifyContent: "space-between" }} horizontal>
            <Dialog.Title>Debug</Dialog.Title>
            <Dialog.CloseIconButton />
          </Stack>
          <ContainerVersionDetails />
        </Stack>
      </Dialog>
    </Box>
  );
};
